import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable, from, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, toArray } from 'rxjs/operators';
import { GuestCartItem } from 'src/app/models/guest-cart.model';

@Injectable({
  providedIn: 'root'
})
export class GuestCartService {

  private readonly STORAGE_KEY = '_GuestCart';
  private itemsSubject: BehaviorSubject<GuestCartItem[]>;

  /** Observable stream of guest cart items for reactive UI updates */
  public items$: BehaviorSubject<GuestCartItem[]>;

  constructor(private injector: Injector) {
    const initialItems = this.readFromStorage();
    this.itemsSubject = new BehaviorSubject<GuestCartItem[]>(initialItems);
    this.items$ = this.itemsSubject;
    this.initServiceListeners();
  }

  private initServiceListeners(): void {
    // 1. Recover pending guest claims on startup
    const pendingClaimStr = localStorage.getItem('pending_guest_claim');
    if (pendingClaimStr) {
      setTimeout(() => {
        try {
          const MessageService = require('primeng/api').MessageService;
          const messageService = this.injector.get(MessageService);
          messageService.add({severity:'success', summary: 'Success', detail: 'Your previous order was successful! Please check your email for account login details.'});
        } catch(e) {}
        localStorage.removeItem('pending_guest_claim');
      }, 3000);
    }

    // 2. Listen to Auth changes to trigger merge automatically
    setTimeout(() => {
      try {
        const AuthService = require('../Auth/auth.service').AuthService;
        const authService = this.injector.get(AuthService);
        authService.currentUserDetails.subscribe((res: any) => {
          if (res && Object.keys(res).length > 0) {
            if (this.getItemCount() > 0) {
              this.mergeGuestCart();
            }
          }
        });
      } catch (e) {}
    }, 1000); // Small delay to ensure auth service is booted
  }

  /**
   * Add an item to the guest cart.
   * If the same beat_id or sound_id already exists, updates the license selection
   * instead of creating a duplicate (mirrors authenticated cart behavior).
   */
  addItem(item: GuestCartItem): void {
    const items = this.getItems();

    const existingIndex = items.findIndex(existing => {
      if (item.beat_id && existing.beat_id) {
        return existing.beat_id === item.beat_id;
      }
      if (item.sound_id && existing.sound_id) {
        return existing.sound_id === item.sound_id;
      }
      return false;
    });

    if (existingIndex > -1) {
      // Update license selection for existing item
      items[existingIndex] = item;
    } else {
      items.push(item);
    }

    this.saveToStorage(items);
    this.itemsSubject.next(items);
  }

  /**
   * Remove an item from the guest cart by index.
   */
  removeItem(index: number): void {
    const items = this.getItems();
    if (index >= 0 && index < items.length) {
      items.splice(index, 1);
      this.saveToStorage(items);
      this.itemsSubject.next(items);
    }
  }

  /**
   * Remove a specific item by beat_id or sound_id.
   */
  removeItemById(beatId?: string, soundId?: string): void {
    let items = this.getItems();
    items = items.filter(item => {
      if (beatId && item.beat_id) {
        return item.beat_id !== beatId;
      }
      if (soundId && item.sound_id) {
        return item.sound_id !== soundId;
      }
      return true;
    });
    this.saveToStorage(items);
    this.itemsSubject.next(items);
  }

  /**
   * Remove several items in a single update, so subscribers react once instead
   * of once per item.
   */
  removeItemsByIds(ids: Array<{ beat_id?: string; sound_id?: string }>): void {
    const beatIds = new Set(ids.map(id => id.beat_id).filter(Boolean));
    const soundIds = new Set(ids.map(id => id.sound_id).filter(Boolean));

    const items = this.getItems();
    const remainingItems = items.filter(item => {
      if (item.beat_id && beatIds.has(item.beat_id)) return false;
      if (item.sound_id && soundIds.has(item.sound_id)) return false;
      return true;
    });

    if (remainingItems.length === items.length) return;

    this.saveToStorage(remainingItems);
    this.itemsSubject.next(remainingItems);
  }

  /**
   * Get a snapshot of all guest cart items.
   */
  getItems(): GuestCartItem[] {
    // Always read from storage for consistency
    return this.readFromStorage();
  }

  /**
   * Get the current guest cart item count.
   */
  getItemCount(): number {
    return this.getItems().length;
  }

  /**
   * Clear the entire guest cart from localStorage.
   */
  clearCart(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (e) {
      // localStorage may be unavailable in some contexts
    }
    this.itemsSubject.next([]);
  }

  /**
   * Check if a beat or sound is already in the guest cart.
   */
  isInCart(beatId?: string, soundId?: string): boolean {
    const items = this.getItems();
    return items.some(item => {
      if (beatId && item.beat_id) return item.beat_id === beatId;
      if (soundId && item.sound_id) return item.sound_id === soundId;
      return false;
    });
  }

  /**
   * Convert guest cart items to the payload format required by POST /order/guest-checkout.
   * Only includes API identifiers (beat_id, beat_price, sound_id, sound_price).
   * Display data is NOT sent to the server.
   */
  toCheckoutPayload(): Array<{ beat_id?: string; beat_price?: string; sound_id?: string; sound_price?: string }> {
    return this.getItems().map(item => {
      const payload: any = {};
      if (item.beat_id) {
        payload.beat_id = item.beat_id;
        payload.beat_price = item.beat_price;
      }
      if (item.sound_id) {
        payload.sound_id = item.sound_id;
        payload.sound_price = item.sound_price;
      }
      return payload;
    });
  }

  /**
   * Process adding an item to the guest cart, including calculating prices,
   * tracking GA4 events, and checking for coupons via API.
   * Returns an Observable so the caller UI can handle routing and popups.
   */
  processAddToCart(beatData: any, selectedLicData: any, isSoundKit: boolean, licType: string): Observable<{
    success: boolean;
    wasAlreadyInCart: boolean;
    availableCoupons: any[];
    requiresLogin: boolean;
  }> {
    // 1. Price calculation
    let displayPrice = 0;
    if (isSoundKit) {
      displayPrice = selectedLicData?.priceDetails?.final_price ?? beatData?.price?.final_price ?? 0;
    } else {
      displayPrice = selectedLicData?.priceDetails?.final_price ?? 0;
    }

    if (isSoundKit && (displayPrice === 0 || displayPrice === null)) {
      return of({ success: false, wasAlreadyInCart: false, availableCoupons: [], requiresLogin: true });
    }

    const subTotal = selectedLicData?.priceDetails?.regular ?? selectedLicData?.priceDetails?.final_price ?? displayPrice;
    const discount = (subTotal - displayPrice) > 0 ? (subTotal - displayPrice) : 0;

    // 2. Build Cart Item
    const guestItem: GuestCartItem = {
      displayData: {
        title: beatData?.title || '',
        cover_picture: beatData?.cover_picture || beatData?.coverImageUrl || beatData?.coverPicture || '',
        producer_name: beatData?.producers?.[0]?.store?.general?.name
          || beatData?.producer?.store?.general?.name
          || beatData?.created_by?.name
          || beatData?.author?.name
          || '',
        producer_tiers: beatData?.producers?.[0]?.tiers 
          || beatData?.producer?.tiers 
          || beatData?.author?.tiers 
          || beatData?.tiers 
          || [],
        producer_handle: beatData?.created_by?.handle
          || beatData?.author?.handle
          || '',
        licence_title: selectedLicData?.title || '',
        licence_slug: selectedLicData?.slug || licType || '',
        final_price: displayPrice,
        sub_total: subTotal,
        discount: discount,
        serial_number: beatData?.serial_number || beatData?.serialNumber || '',
        type: isSoundKit ? 'sound' : 'beat',
        category: beatData?.category || beatData?.genre || beatData?.genres?.[0] || '',
        tempo: beatData?.tempo || 0,
        available_coupons: beatData?.available_coupons || []
      }
    };

    if (isSoundKit) {
      guestItem.sound_id = beatData._id;
      guestItem.sound_price = selectedLicData?.priceDetails?._id || '';
    } else {
      guestItem.beat_id = beatData._id;
      guestItem.beat_price = selectedLicData?.priceDetails?._id || '';
    }

    const wasAlreadyInCart = this.isInCart(
      isSoundKit ? undefined : beatData._id,
      isSoundKit ? beatData._id : undefined
    );

    this.addItem(guestItem);

    // 3. Track GA4 Event
    try {
      const Ga4trackingService = require('../ga4Tracking/ga4tracking.service').Ga4trackingService;
      const ga4Tracking = this.injector.get(Ga4trackingService);
      const eventName = isSoundKit ? 'add_to_cart_sound_buy' : 'add_to_cart';
      const attributes: any = {
        category: isSoundKit ? 'Sound' : 'Beats',
        label: 'cart',
        action: 'click',
        value: displayPrice,
        licType: licType,
        genre: beatData?.genre || beatData?.genres?.[0],
        userRole: 'guest',
        userId: 'guest',
        checkoutType: 'guest'
      };
      if (isSoundKit) {
        attributes['soundName'] = beatData?.title;
        attributes['soundId'] = beatData?._id;
      } else {
        attributes['beatName'] = beatData?.title;
        attributes['beatId'] = beatData?._id;
      }
      ga4Tracking.sendEvent(eventName, attributes);
    } catch (e) {
      console.warn('GA4 tracking failed in GuestCartService', e);
    }

    // 4. API Call for Coupons (using CartService lazily)
    let cartService: any;
    try {
      const CartService = require('./cart.service').CartService;
      cartService = this.injector.get(CartService);
    } catch (e) {
      return of({ success: true, wasAlreadyInCart, availableCoupons: [], requiresLogin: false });
    }

    const payload = this.toCheckoutPayload();
    return cartService.getGuestCartPreview(payload).pipe(
      map((res: any) => {
        const addedItem = res.carts.find((c: any) => 
          (c.beat && c.beat._id === beatData._id) || 
          (c.sound && c.sound._id === beatData._id)
        );
        const availableCoupons = addedItem?.beat?.available_coupons || addedItem?.sound?.available_coupons || [];
        return { success: true, wasAlreadyInCart, availableCoupons, requiresLogin: false };
      }),
      catchError(() => {
        return of({ success: true, wasAlreadyInCart, availableCoupons: [], requiresLogin: false });
      })
    );
  }

  /**
   * Read guest cart from localStorage with defensive parsing.
   */
  private readFromStorage(): GuestCartItem[] {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (e) {
      // Corrupted data — clear and start fresh
      try {
        localStorage.removeItem(this.STORAGE_KEY);
      } catch (_) {}
    }
    return [];
  }

  /**
   * Save guest cart to localStorage.
   */
  private saveToStorage(items: GuestCartItem[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      // localStorage quota exceeded or unavailable
      console.warn('[GuestCartService] Failed to save to localStorage', e);
    }
  }

  /**
   * Merge local guest cart items into the authenticated user's server cart.
   * Called after login when guest cart has items.
   * Uses lazy injection to avoid circular DI with CartService and CommonService.
   */
  public mergeGuestCart(): void {
    const guestItems = this.getItems();
    if (guestItems.length === 0) return;

    // Lazy-resolve to avoid circular dependency
    const CartService = require('./cart.service').CartService;
    const CommonService = require('../Common-function/common.service').CommonService;
    const cartService = this.injector.get(CartService);
    const commonFunc = this.injector.get(CommonService);

    cartService.getCarts().pipe(
      switchMap((serverCart: any) => {
        const serverItems = serverCart?.carts || [];
        const deleteObservables: Observable<any>[] = [];
        const insertTasks: { item: GuestCartItem; payload: any }[] = [];

        guestItems.forEach((guestItem: GuestCartItem) => {
          const existingServerItem = serverItems.find((si: any) => {
            if (guestItem.beat_id && si.beat) return si.beat._id === guestItem.beat_id;
            if (guestItem.sound_id && si.sound) return si.sound._id === guestItem.sound_id;
            return false;
          });

          if (existingServerItem) {
            const sameLicense =
              (guestItem.beat_id && existingServerItem.beat_price?._id === guestItem.beat_price) ||
              (guestItem.sound_id && existingServerItem.sound_price?._id === guestItem.sound_price);

            if (!sameLicense) {
              deleteObservables.push(
                cartService.deleteCartData(existingServerItem._id).pipe(catchError(() => of(null)))
              );
              insertTasks.push({ item: guestItem, payload: this.buildInsertPayload(guestItem) });
            } else {
              this.removeItemById(guestItem.beat_id, guestItem.sound_id);
            }
          } else {
            insertTasks.push({ item: guestItem, payload: this.buildInsertPayload(guestItem) });
          }
        });

        let processDeletions$: Observable<any>;
        if (deleteObservables.length > 0) {
          processDeletions$ = from(deleteObservables).pipe(concatMap(obs => obs), toArray());
        } else {
          processDeletions$ = of(null);
        }

        let processInsertions$: Observable<any>;
        if (insertTasks.length > 0) {
          processInsertions$ = from(insertTasks).pipe(
            concatMap(task =>
              cartService.insertCartData(task.payload).pipe(
                map(() => {
                  this.removeItemById(task.item.beat_id, task.item.sound_id);
                  return true;
                }),
                catchError((err: any) => {
                  console.error('[GuestCartService] Failed to insert item', task.item, err);
                  return of(false);
                })
              )
            ),
            toArray()
          );
        } else {
          processInsertions$ = of([]);
        }

        return processDeletions$.pipe(
          switchMap(() => processInsertions$)
        );
      })
    ).subscribe({
      next: (results: any) => {
        if (Array.isArray(results) && results.some((res: any) => res === false)) {
          commonFunc.showToastErrorMessage('Some items could not be merged to your account. Please check your cart.');
        }

        cartService.getCarts().subscribe((updatedCart: any) => {
          commonFunc.cartDataList.next(updatedCart);
        });
      },
      error: (err: any) => {
        console.error('Error during guest cart merge', err);
        commonFunc.showToastErrorMessage('Failed to merge guest cart. Items are saved locally.');
      }
    });
  }

  /**
   * Build the insert payload for the cart API from a guest cart item.
   */
  private buildInsertPayload(item: GuestCartItem): any {
    const payload: any = {};
    if (item.beat_id) {
      payload.beat_id = item.beat_id;
      payload.beat_price = item.beat_price;
    }
    if (item.sound_id) {
      payload.sound_id = item.sound_id;
      payload.sound_price = item.sound_price;
    }
    return payload;
  }
}
